import { useEffect, useState } from "react";
import {
  getAdjustment2Reference,
  getCalls,
  getManualGroupRefrenceSetting,
  getPuts,
  setAdjustment2Reference,
} from "../../../Requests/deltaHedgeAdjustment";
import { IAdjustment2Reference } from "../../../Interfaces/deltaHedgeAdjustment";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { toast } from "react-toastify";
import TextInput from "../../../Components/TextInput";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import Button from "../../../Components/Button";
import DynamicDropdown from "../../../Components/Dropdown/DynamicDropdown";
import { getGroups } from "../../../Requests/optionDomain";
import CheckBox from "../../../Components/Checkbox";

interface IProps {
  onClose: () => void;
  groupId: string | number;
}

const ReferenceConfig = ({ onClose, groupId }: IProps) => {
  const [formValues, setFormValues] = useState({
    groupId,
    call: { checkBaseInstrument: false, checkDueDate: false },
    put: { checkBaseInstrument: false, checkDueDate: false },
  } as IAdjustment2Reference);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!formValues.groupId) return;
    setLoading(true);
    getAdjustment2Reference(formValues.groupId)
      .then((resp) => {
        setFormValues({
          ...formValues,
          ...resp,
          groupId: resp.groupId ?? formValues.groupId,
        });
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
    getManualGroupRefrenceSetting(formValues.groupId)
      .then((resp) => {
        setFormValues({ ...formValues, ...resp });
      })
      .catch(packetErrorHandler);
  }, [formValues.groupId]);

  const submitHandler = () => {
    setLoading(true);
    setAdjustment2Reference(formValues, formValues.groupId)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onClose();
        } else toast.error(resp.msg);
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const changeHandler = (name: string, value: any) => {
    const nameParts = name.split(".");
    setFormValues({
      ...formValues,
      [nameParts[0]]: {
        ...formValues[
          nameParts[0] as Exclude<
            keyof IAdjustment2Reference,
            "groupId" | "callInsId1" | "callInsId2" | "putInsId1" | "putInsId2"
          >
        ],
        [nameParts[1]]: value,
      } as any,
    } as IAdjustment2Reference);
  };

  const normalChangeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const getForm = (
    title: string,
    fieldName: Exclude<
      keyof IAdjustment2Reference,
      "groupId" | "callInsId1" | "callInsId2" | "putInsId1" | "putInsId2"
    >
  ) => (
    <div className="grid grid-cols-5 gap-x-2 gap-y-3 w-full pb-3 items-end border-b border-gray-400">
      <div className="font-bold">{title}</div>
      <div />
      <div />
      <div />
      <div />

      <TextInput
        label="حداقل حجم روز معاملاتی قبل"
        name={`${fieldName}.minLastTrade`}
        value={formValues[fieldName]?.minLastTrade?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل روز تا سررسید"
        name={`${fieldName}.minRemainingDay`}
        value={formValues[fieldName]?.minRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداکثر روز تا سررسید"
        name={`${fieldName}.maxRemainingDay`}
        value={formValues[fieldName]?.maxRemainingDay?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <TextInput
        label="حداقل قدر مطلق دلتا"
        name={`${fieldName}.minAbsDelta`}
        value={formValues[fieldName]?.minAbsDelta?.toString()}
        onChange={changeHandler}
        extraLabel
      />
      <CheckBox
        checked={formValues[fieldName]?.checkBaseInstrument}
        label="الزام به همجنس بودن"
        name={`${fieldName}.checkBaseInstrument`}
        onChange={changeHandler}
      />
      <CheckBox
        checked={formValues[fieldName]?.checkDueDate}
        label="الزام به هم رسید بودن"
        name={`${fieldName}.checkDueDate`}
        onChange={changeHandler}
      />
    </div>
  );

  return (
    <div className="flex flex-col justify-center items-start gap-3">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="w-[300px]">
        <DynamicDropdown
          dropdown={{
            name: "groupId",
            placeholder: "گروه",
            extraLabel: true,
          }}
          getData={getGroups}
          onChange={normalChangeHandler}
          value={formValues.groupId?.toString()}
        />
      </div>
      <div className="border-b border-gray-400 gap-x-2 gap-y-3 w-full pb-3" />

      {getForm("Call Spread", "call")}
      {getForm("Put Spread", "put")}
      <div className="flex flex-col justify-start items-start gap-2 w-full">
        <div className="font-bold">انتخاب دستی</div>

        <div className="flex flex-row justify-start items-center gap-2 w-full">
          <div>CS</div>
          <div className="w-64">
            <DynamicDropdown
              dropdown={{
                name: "callInsId1",
                placeholder: "کال یک",
                extraLabel: true,
                disabled: !formValues.groupId,
              }}
              getData={getCalls}
              onChange={normalChangeHandler}
              value={formValues.callInsId1?.toString()}
              inputId={formValues.groupId}
            />
          </div>
          <div className="w-64">
            <DynamicDropdown
              dropdown={{
                name: "callInsId2",
                placeholder: "کال دو",
                extraLabel: true,
                disabled: !formValues.groupId,
              }}
              getData={getCalls}
              onChange={normalChangeHandler}
              value={formValues.callInsId2?.toString()}
              inputId={formValues.groupId}
            />
          </div>
        </div>
        <div className="flex flex-row justify-start items-center gap-2 w-full">
          <div>PS</div>
          <div className="w-64">
            <DynamicDropdown
              dropdown={{
                name: "putInsId1",
                placeholder: "پوت یک",
                extraLabel: true,
                disabled: !formValues.groupId,
              }}
              getData={getPuts}
              onChange={normalChangeHandler}
              value={formValues.putInsId1?.toString()}
              inputId={formValues.groupId}
            />
          </div>
          <div className="w-64">
            <DynamicDropdown
              dropdown={{
                name: "putInsId2",
                placeholder: "پوت دو",
                extraLabel: true,
                disabled: !formValues.groupId,
              }}
              getData={getPuts}
              onChange={normalChangeHandler}
              value={formValues.putInsId2?.toString()}
              inputId={formValues.groupId}
            />
          </div>
        </div>
      </div>
      <div className="mr-auto w-24">
        <Button text="ثبت" onClick={submitHandler} variant="contained" />
      </div>
    </div>
  );
};

export default ReferenceConfig;
